<template>
  <div class="website-wrapper">
    <div class="website-header">
      <div class="main-title">Websites</div>
      <div style="display: flex; gap: 10px; align-items: center">
        <el-input
          v-model="search"
          placeholder="Search website"
          prefix-icon="el-icon-search"
          clearable
          @clear="handleClearSearch"
          @input="handleSearch"
        ></el-input>
        <el-button type="primary" @click="addWebsiteDialogVisible = true"
          >Add new website</el-button
        >
        <el-divider direction="vertical"></el-divider>
        <div style="display: flex; gap: 5px; align-items: center">
          <el-button
            icon="el-icon-download"
            type="primary"
            @click="openExportDialog"
            :loading="isLoadingExportImport"
            :disabled="isLoading || websites.length === 0 || isLoadingExportImport"
          >
            Export
          </el-button>

          <el-button
            icon="el-icon-upload"
            type="primary"
            @click="openImportDialog"
            style="margin-left: 5px"
            :loading="isLoadingExportImport"
            :disabled="isLoading || isLoadingExportImport"
          >
            Import
          </el-button>
        </div>
      </div>
    </div>
    <div class="website-body">
      <el-table :data="websites" v-loading="isLoading">
        <el-table-column type="index"></el-table-column>
        <el-table-column prop="title" label="Title">
          <template slot-scope="scope">
            <span style="margin-right: 10px">{{ scope.row.title }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="url" label="Url"></el-table-column>
        <el-table-column prop="tags" label="Tags" width="200">
          <template slot-scope="scope">
            <div>
              <div style="margin-bottom: 15px" v-for="(tags, name) in scope.row.tags" :key="name">
                <span>{{ name }} : </span>
                <el-tag v-for="(tag, index) in tags" :key="index" style="margin-right: 10px">{{
                  tag
                }}</el-tag>
              </div>
              <el-button
                size="mini"
                type="primary"
                @click="handleOpenTagEditor(scope.row)"
                v-if="isAbleToEdit(scope.row)"
              >
                <i class="el-icon-edit"></i>
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="uploadedBy" label="Updated By" width="200">
          <template slot-scope="scope">
            {{ scope.row.uploadedBy || "N/A" }}
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="Updated At" width="200">
          <template slot-scope="scope">
            {{ formattedDate(scope.row.updatedAt) }}
          </template>
        </el-table-column>
        <el-table-column label="Actions">
          <template slot-scope="scope">
            <div class="table-wrapper" v-if="isAbleToEdit(scope.row)" style="overflow-x: auto">
              <el-button type="primary" size="small" @click="openRefreshDialog(scope.row)">
                Schedule Refresh
              </el-button>

              <el-dropdown trigger="click" @command="handleDropdownCommand">
                <el-button icon="el-icon-more" size="small"></el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item
                      :command="{ type: 're-scrape', row: scope.row }"
                      :disabled="isScrapingWebsite && currentScrapeWebsiteId === scope.row.id"
                    >
                      <span v-if="isScrapingWebsite && currentScrapeWebsiteId === scope.row.id">
                        <el-icon><Loading /></el-icon> Re-scrape
                      </span>
                      <span v-else>Re-scrape</span>
                    </el-dropdown-item>

                    <el-dropdown-item
                      :command="{ type: 'status', row: scope.row }"
                      :disabled="isFetchDocumentStatus"
                    >
                      <span v-if="isFetchDocumentStatus">
                        <el-icon><Loading /></el-icon> Status
                      </span>
                      <span v-else>Status</span>
                    </el-dropdown-item>

                    <el-dropdown-item
                      v-if="scope.row.content?.length > 0"
                      :command="{ type: 'upload', row: scope.row }"
                    >
                      Upload
                    </el-dropdown-item>

                    <el-dropdown-item :command="{ type: 'edit', row: scope.row }">
                      Edit
                    </el-dropdown-item>

                    <el-dropdown-item
                      :command="{ type: 'delete', row: scope.row }"
                      divided
                      style="color: red"
                    >
                      Delete
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
            <div class="table-wrapper" v-else>
              <el-button
                type="primary"
                size="small"
                style="margin-right: 10px"
                @click="openStatusDialog(scope.row)"
                :loading="isFetchDocumentStatus"
                :disabled="isFetchDocumentStatus"
                >Status</el-button
              >
              <el-button
                icon="el-icon-edit"
                s
                @click="handleEditWebsite(scope.row, false)"
                size="small"
                type="warning"
                >Details</el-button
              >
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="website-footer" v-if="websitesLength > 0 && !inSearchMode">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="websitesLength"
        :page-size="10"
        @current-change="handleCurrentPageChange"
        :currentPage.sync="currentPage"
      ></el-pagination>
    </div>
    <Editor
      v-if="editorVisible"
      :website="selectedWebsite"
      :editorVisible="editorVisible"
      :isEditMode="isEditMode"
      @updateEditorVisible="updateEditorVisible"
      @handleUpdatedWebsite="handleUpdatedWebsite"
      :isEditorDialogLoading="isEditorDialogLoading"
    />
    <AddWebsite
      v-if="addWebsiteDialogVisible"
      :addWebsiteDialogVisible="addWebsiteDialogVisible"
      @updateEditorVisible="updateEditorVisible"
      @handleCreateWebsite="handleCreateWebsite"
      :isWebsiteDialogLoading="isWebsiteDialogLoading"
    />
    <DocumentStatusModal
      v-if="statusDialogVisible && isSelectedDocumentUpload"
      :statusDialogVisible="statusDialogVisible"
      @updateStatusDialogVisible="updateStatusDialogVisible"
      @refreshDocumentStatus="refreshDocumentStatus"
      :selectedDocumentStatus="selectedDocumentStatus"
      :isRefreshingStatus="isRefreshingStatus"
      :isWebsite="true"
      :selectedDocument="selectedWebsite"
    />
    <document-status-not-upload-modal
      v-else-if="statusDialogVisible && !isSelectedDocumentUpload"
      @updateStatusDialogVisible="updateStatusDialogVisible"
      :statusDialogVisible="statusDialogVisible"
      :isWebsite="true"
      :selectedWebsite="selectedWebsite"
    />

    <TagEditor
      :shouldShowTagEditor="shouldShowTagEditor"
      :selectedWebsite="selectedWebsite"
      :isLoading="isLoading"
      @handleCloseTagEditor="handleCloseTagEditor"
      @handleUpdateWebsiteTag="handleUpdateWebsiteTag"
    />

    <ExportDialog
      v-if="exportDialogVisible"
      :dialogVisible="exportDialogVisible"
      :documents="exportWebsites"
      exportType="websites"
      @updateDialogVisible="updateExportDialogVisible"
    />

    <ImportDialog
      v-if="importDialogVisible"
      importType="websites"
      :dialogVisible="importDialogVisible"
      :isLoading="isLoadingExportImport"
      @updateDialogVisible="updateImportDialogVisible"
      @import="handleImport"
    />

    <WebsiteContentRefreshDialog
      v-if="refreshDialogVisible"
      :dialogVisible="refreshDialogVisible"
      :website="selectedWebsite"
      @saveSchedule="handleUpdatedWebsite"
      @updateDialogVisible="updateRefreshDialogVisible"
    />
  </div>
</template>

<script>
import Editor from "./Editor.vue";
import AddWebsite from "./AddWebsite.vue";
import DocumentStatusModal from "../Documents/DocumentStatusModal.vue";
import DocumentStatusNotUploadModal from "../Documents/DocumentStatusNotUploadModal.vue";
import ExportDialog from "../ExportDialog.vue";
import ImportDialog from "../ImportDialog.vue";
import TagEditor from "./TagEditor.vue";
import { mapGetters } from "vuex";
import moment from "moment";
import WebsiteContentRefreshDialog from "./WebsiteContentRefreshDialog.vue";

export default {
  components: {
    Editor,
    AddWebsite,
    DocumentStatusModal,
    DocumentStatusNotUploadModal,
    TagEditor,
    ExportDialog,
    ImportDialog,
    WebsiteContentRefreshDialog,
  },
  data() {
    return {
      editorVisible: false,
      addWebsiteDialogVisible: false,
      selectedWebsite: {},
      websitesLength: 0,
      isWebsiteDialogLoading: false,
      isEditorDialogLoading: false,
      isLoading: false,
      selectedDocumentStatus: null,
      isFetchDocumentStatus: false,
      isRefreshingStatus: false,
      statusDialogVisible: false,
      isSelectedDocumentUpload: false,
      shouldShowTagEditor: false,
      currentPage: 1,
      search: "",
      inSearchMode: false,
      isEditMode: false,
      allowEditRoles: ["admin", "genai-editor"],
      importDialogVisible: false,
      exportDialogVisible: false,
      isLoadingExportImport: false,
      exportWebsites: [],
      isScrapingWebsite: false,
      currentScrapeWebsiteId: null,
      refreshDialogVisible: false,
    };
  },
  computed: {
    ...mapGetters(["getUserAuthentication"]),
    websites() {
      const websites = _.cloneDeep(this.$store.state.genai.genai_websites);
      websites.forEach((website) => {
        if (website.tags) {
          website.tags = JSON.parse(website.tags);
        } else {
          website.tags = {};
        }
      });
      return websites;
    },
    userProfile() {
      return this.$store.state.profile;
    },
  },
  methods: {
    handleEditWebsite(website, isEdit = true) {
      this.selectedWebsite = website;
      this.editorVisible = true;
      this.isEditMode = isEdit;
    },
    updateEditorVisible({ dialog, status }) {
      switch (dialog) {
        case "editor":
          this.editorVisible = status;
          break;
        case "addWebsite":
          this.addWebsiteDialogVisible = status;
          break;
        default:
          break;
      }
    },
    async handleCreateWebsite(website) {
      this.isWebsiteDialogLoading = true;
      const result = await this.$store.dispatch("GENAI_CREATE_WEBSITE", website);
      this.isWebsiteDialogLoading = false;
      this.addWebsiteDialogVisible = false;
      if (result.success) {
        this.$message({
          type: "success",
          message: "Create website successfully!",
        });
      } else {
        this.$message({
          type: "error",
          message: "Failed to create website",
        });
      }
    },
    async handleUploadWebsite(id) {
      this.$confirm("Are you sure to upload this website?", "Warning", {
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        type: "warning",
      }).then(async () => {
        try {
          this.isLoading = true;
          const uploadedBy = this.userProfile.email;
          const result = await this.$store.dispatch("GENAI_UPLOAD_WEBSITE", {
            id,
            uploadedBy,
            page: this.currentPage,
          });
          if (result.success) {
            this.$message({
              type: "success",
              message: "Upload successfully!",
            });
          }
          this.isLoading = false;
        } catch (error) {
          this.isLoading = false;
          this.$notify({
            title: "Error",
            message: "There was an error uploading the website",
            type: "error",
          });
        }
      });
    },
    handleDeleteWebsite(website) {
      this.$confirm("Are you sure to delete this website?", "Warning", {
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        type: "warning",
      }).then(async () => {
        try {
          this.isLoading = true;
          const result = await this.$store.dispatch("GENAI_DELETE_WEBSITE", {
            id: website.id,
            name: website.title,
          });
          if (result.success) {
            this.$message({
              type: "success",
              message: "Delete successfully!",
            });
          }
          this.isLoading = false;
        } catch (error) {
          this.isLoading = false;
          this.$notify({
            title: "Error",
            message: "There was an error deleting the website",
            type: "error",
          });
        }
      });
    },
    async handleUpdatedWebsite(newWebsite) {
      this.isLoading = true;
      this.isEditorDialogLoading = true;
      const result = await this.$store.dispatch("GENAI_UPDATE_WEBSITE", {
        newWebsite,
        page: this.currentPage,
      });
      if (result.success) {
        this.isEditorDialogLoading = false;
        this.editorVisible = false;
        this.isLoading = false;
        this.$message({
          type: "success",
          message: "Update successfully!",
        });
      } else {
        this.isLoading = false;
        this.isEditorDialogLoading = false;
        this.$notify({
          title: "Error",
          message: "There was an error updating the article",
          type: "error",
        });
      }
    },
    async handleCurrentPageChange(page) {
      this.isLoading = true;
      await this.$store.dispatch("GENAI_GET_WEBSITES", { page });
      this.isLoading = false;
    },
    updateStatusDialogVisible(value) {
      this.statusDialogVisible = value;
    },
    async openStatusDialog(website) {
      try {
        this.isLoading = true;
        if (website.isUploaded) {
          const documentStatus = await this.$store.dispatch("GENAI_GET_DOCUMENT_STATUS", {
            documentId: website.id,
          });
          this.selectedDocumentStatus = documentStatus;
        }
        this.isSelectedDocumentUpload = website.isUploaded;
        this.selectedWebsite = website;
        this.statusDialogVisible = true;
        this.isLoading = false;
      } catch (error) {
        this.$notify({
          title: "Error",
          message: "There was an error getting the document status",
          type: "error",
        });
        this.isLoading = false;
      }
    },
    async refreshDocumentStatus(documentId) {
      this.isRefreshingStatus = true;
      const documentStatus = await this.$store.dispatch("GENAI_GET_DOCUMENT_STATUS", {
        documentId,
      });
      this.selectedDocumentStatus = documentStatus;
      this.isRefreshingStatus = false;
      return documentStatus;
    },
    handleCloseTagEditor() {
      this.shouldShowTagEditor = false;
    },
    handleOpenTagEditor(website) {
      this.shouldShowTagEditor = true;
      this.selectedWebsite = website;
    },
    async handleUpdateWebsiteTag(newWebsite) {
      this.isLoading = true;
      if (newWebsite.tags) {
        newWebsite.tags = JSON.stringify(newWebsite.tags);
      }
      const result = await this.$store.dispatch("GENAI_UPDATE_WEBSITE", {
        newWebsite,
        page: this.currentPage,
      });
      if (result.success) {
        this.shouldShowTagEditor = false;
        this.isLoading = false;
        this.$message({
          type: "success",
          message: "Update website successfully!",
        });
      } else {
        this.$message({
          type: "error",
          message: "Failed to update website",
        });
        this.isLoading = false;
      }
    },
    handleSearch: _.debounce(async function () {
      try {
        this.isLoading = true;
        if (this.search === "") {
          await this.$store.dispatch("GENAI_GET_WEBSITES", { page: 1 });
          this.inSearchMode = false;
          this.isLoading = false;
          return;
        }
        await this.$store.dispatch("GENAI_SEARCH_DOCUMENTS", {
          search: this.search,
          documentType: "websites",
        });
        this.isLoading = false;
        this.inSearchMode = true;
      } catch (error) {
        this.inSearchMode = false;
        this.isLoading = false;
        this.$notify({
          title: "Error",
          message: "There was an error searching the website",
          type: "error",
        });
      }
    }, 800),

    async handleClearSearch() {
      try {
        this.search = "";
        await this.$store.dispatch("GENAI_GET_WEBSITES", { page: 1 });
        this.isLoading = false;
        this.inSearchMode = false;
      } catch (error) {
        this.isLoading = false;
        this.$notify({
          title: "Error",
          message: "There was an error clearing the search",
          type: "error",
        });
      }
    },
    isAbleToEdit(website) {
      return (
        this.userProfile.email === website.uploadedBy ||
        this.getUserAuthentication["roles"].some((role) => this.allowEditRoles.includes(role))
      );
    },
    formattedDate(date) {
      return moment(date).format("DD/MM/YYYY hh:mm A");
    },
    async openExportDialog() {
      this.isLoadingExportImport = true;
      const data = await this.$store.dispatch("GENAI_GET_WEBSITES_ABLE_TO_EXPORT");
      this.exportWebsites = data.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      this.exportDialogVisible = true;
      this.isLoadingExportImport = false;
    },
    openImportDialog() {
      this.importDialogVisible = true;
    },
    updateExportDialogVisible(value) {
      this.exportDialogVisible = value;
    },
    updateImportDialogVisible(value) {
      this.importDialogVisible = value;
    },
    async handleImport(websites) {
      this.isLoadingExportImport = true;
      const uploadedBy = this.userProfile.email;
      const allWebsite = await this.$store.dispatch("GENAI_GET_WEBSITES_ABLE_TO_EXPORT");
      const duplicatedWebsites = websites.filter((website) =>
        allWebsite.some((w) => w.title === website.title)
      );
      if (duplicatedWebsites.length > 0) {
        const h = this.$createElement;
        this.$msgbox({
          title: "Warning",
          message: h("div", null, [
            h(
              "p",
              { style: { margin: "10px 0" } },
              "Duplicate website found. Do you want to continue?"
            ),
            h("p", { style: { marginBottom: "10px" } }, `Duplicated Websites: `),
            h(
              "div",
              {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: "200px",
                  overflowY: "auto",
                  borderRadius: "5px",
                  border: "1px solid #e5e5e5",
                  padding: "10px",
                },
              },
              duplicatedWebsites.map((d) =>
                h("p", { style: { fontWeight: "bold", margin: "5px 0" } }, d.title)
              )
            ),
          ]),
          showCancelButton: true,
          confirmButtonText: "Skip Duplicates",
          cancelButtonText: "Cancel",
        })
          .then(async () => {
            const uniqueWebsites = websites.filter(
              (website) => !duplicatedWebsites.some((d) => d.title === website.title)
            );
            if (uniqueWebsites.length > 0) {
              await this.$store.dispatch("GENAI_IMPORT_WEBSITES", {
                websites: uniqueWebsites,
                uploadedBy,
              });
              this.$notify({
                title: "Success",
                message: "Websites imported successfully",
                type: "success",
              });
            } else {
              this.$notify({
                title: "Error",
                message: "No websites to import",
                type: "error",
              });
            }
            this.importDialogVisible = false;
            this.isLoadingExportImport = false;
          })
          .catch(() => {
            this.isLoadingExportImport = false;
          });
        return;
      }

      await this.$store
        .dispatch("GENAI_IMPORT_WEBSITES", { websites, uploadedBy })
        .then(() => {
          this.isLoadingExportImport = false;
          this.importDialogVisible = false;
          this.$message.success("Websites imported successfully");
        })
        .catch(() => {
          this.isLoadingExportImport = false;
          this.$message.error("Failed to import websites");
        });
    },
    handleReScrapeWebsite(website) {
      this.isScrapingWebsite = true;
      this.currentScrapeWebsiteId = website.id;
      this.$store
        .dispatch("GENAI_RESCRAPE_WEBSITE", { id: website.id })
        .then(async (resp) => {
          if (resp.success) {
            await this.$store.dispatch("GENAI_GET_WEBSITES", { page: this.currentPage });

            this.$message({
              type: "success",
              message: "Update website successfully!",
            });
          }
        })
        .catch((error) => {
          this.$message({
            type: "error",
            message: "Update website failed!",
          });
        })
        .finally(() => {
          this.isScrapingWebsite = false;
          this.currentScrapeWebsiteId = null;
        });
    },
    openRefreshDialog(website) {
      this.refreshDialogVisible = true;
      this.selectedWebsite = website;
    },
    updateRefreshDialogVisible(visible) {
      this.refreshDialogVisible = visible;
    },
    handleDropdownCommand({ type, row }) {
      switch (type) {
        case "re-scrape":
          this.handleReScrapeWebsite(row);
          break;
        case "status":
          this.openStatusDialog(row);
          break;
        case "upload":
          this.handleUploadWebsite(row.id);
          break;
        case "edit":
          this.handleEditWebsite(row);
          break;
        case "delete":
          this.handleDeleteWebsite(row);
          break;
      }
    },
  },
  async created() {
    this.isLoading = true;
    const websites = await this.$store.dispatch("GENAI_GET_WEBSITES", { page: 1 });
    this.websitesLength = websites.count;
    this.isLoading = false;
  },
};
</script>

<style lang="scss">
.website-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.website-body {
  padding: 20px;
}

.website-wrapper {
  margin: 20px;
}

.table-wrapper {
  display: flex;
  gap: 15px;
  flex-wrap: no-wrap;

  button {
    margin: 0 !important;
  }
}
</style>
